/**
 * Plan-then-approve helpers
 *
 * Structured propose_plan steps + mutate-tool gate until a human Approves.
 * Reuses waiting_approval semantics (status awaiting_approval on chat_plans).
 *
 * Primary responsibilities:
 * - Parse / serialize plan steps (read vs mutate)
 * - Decide when mutate tools are blocked
 * - Format markdown content from structured steps for Canvas
 *
 * Dependencies:
 * - lib/crew/computer-plan-gate-prep (C2 PR1 computer mutate ids)
 */

import { CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS, CREW_C2_PROPOSED_PLAN_GATED_COMPUTER_TOOL_IDS } from '@/lib/crew/computer-plan-gate-prep'

export type PlanStepKind = 'read' | 'mutate'

export type ProposedPlanStep = {
  id: string
  title: string
  kind: PlanStepKind
  detail?: string | null
}

export type ChatPlanApprovalStatus =
  | 'draft'
  | 'awaiting_approval'
  | 'active'
  | 'completed'
  | 'cleared'
  | 'rejected'

export const PLAN_AWAITING_APPROVAL: ChatPlanApprovalStatus = 'awaiting_approval'

/** In-thread card title — Space + computer (C2 PR1). */
export const PLAN_APPROVAL_CARD_TITLE =
  'Approve before Losi changes Space items or runs computer actions' as const

/** Footer under plan steps while awaiting. */
export const PLAN_APPROVAL_CARD_FOOTER =
  'Space and computer mutate tools stay blocked until you Approve.' as const

/** Mutate step badge — generic (Space or computer). */
export const PLAN_APPROVAL_STEP_MUTATE_BADGE = 'writes / mutates' as const

export const PLAN_APPROVAL_STEP_READ_BADGE = 'reads only' as const

/** Same-turn tools-stream block when a gated mutate fires while awaiting. */
export const PLAN_GATE_SAME_TURN_BLOCKED_MESSAGE =
  'Blocked: this plan is awaiting approval. Mutate Space and computer tools cannot run until the user Approves the plan.' as const

/** Model-facing note while plan is awaiting_approval. */
export const PLAN_GATE_AWAITING_MODEL_NOTE =
  'This plan is awaiting_approval. Do NOT call mutate Space or computer tools until the user Approves. You may call propose_plan again to revise.' as const

/** propose_plan tool description — STOP + gate honesty. */
export const PLAN_PROPOSE_TOOL_DESCRIPTION =
  'Propose a structured Losi Execution Plan (steps Losi will run with tools in this chat) and STOP. Sets status awaiting_approval. Do NOT call mutate Space or computer tools until the user Approves in the plan card. Prefer this when the soul rule says propose before mutating, or the user asked for a plan first for in-product work. Do NOT use for real-life / offline plans the human will do themselves (trip, fitness, wedding, career, etc.) — those belong in chat markdown or a saved note/file, not this tool.' as const

/** Saved propose_plan tool result message. */
export const PLAN_PROPOSE_SAVED_MESSAGE =
  'Plan saved as awaiting_approval. Stop mutate Space and computer tools until the user Approves.' as const

/** Markdown footer under proposed plan Canvas content. */
export const PLAN_AWAITING_MARKDOWN_FOOTER =
  '_Waiting for Approve before mutate tools run._' as const

/**
 * Space / chat mutate tool ids that must not run while a plan awaits approval.
 * Read-only tools (list/get/search) stay available.
 * C2 PR1: also includes sandbox / canvas-browser / desktop-computer mutates
 * from `CREW_C2_PROPOSED_PLAN_GATED_COMPUTER_TOOL_IDS`. PR1.1: also includes
 * desktop filesystem mutates from
 * `CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS` (tools-stream already filters +
 * same-turn blocks via isMutateToolGatedByPlan).
 */
export const PLAN_GATED_MUTATE_TOOL_IDS = [
  'createSpace',
  'updateSpace',
  'archiveSpace',
  'createSpaceTask',
  'updateSpaceTask',
  'deleteSpaceTask',
  'createTaskComment',
  'createTaskSubtask',
  'updateTaskSubtask',
  'deleteTaskSubtask',
  'createTaskChecklistItem',
  'updateTaskChecklistItem',
  'deleteTaskChecklistItem',
  'deleteTaskNote',
  'attachTaskLinkedItem',
  'removeTaskLinkedItem',
  'createSpaceNote',
  'updateSpaceNote',
  'appendSpaceNote',
  'upsertSpaceNote',
  'deleteSpaceNote',
  'proposeResolveComment',
  'leaveInlineFeedback',
  'createSpaceDesign',
  'updateSpaceDesign',
  'deleteSpaceDesign',
  'applySpaceDesignSceneMutation',
  'createSpaceSheet',
  'updateSpaceSheet',
  'deleteSpaceSheet',
  'replaceSpaceSheetWorkbook',
  'applySpaceSheetRangeUpdate',
  'applySpaceSheetRangeFill',
  'applySpaceSheetRangeTransfer',
  'applySpaceSheetStructureUpdate',
  'applySpaceSheetDimensionUpdate',
  'applySpaceSheetVisibilityUpdate',
  'createSpaceFolder',
  'updateSpaceFolder',
  'moveSpaceFolder',
  'createSpaceSprint',
  'updateSpaceSprint',
  'createSpaceEvent',
  'updateSpaceEvent',
  'createManagedCalendarEvent',
  'updateManagedCalendarEvent',
  'deleteManagedCalendarEvent',
  'attachSpaceFile',
  'updateSpaceFile',
  'detachSpaceFile',
  'attachSpaceCollection',
  'updateSpaceCollection',
  'detachSpaceCollection',
  ...CREW_C2_PROPOSED_PLAN_GATED_COMPUTER_TOOL_IDS,
  ...CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS
] as const

const GATED_SET = new Set<string>(PLAN_GATED_MUTATE_TOOL_IDS)

// Bare function ids (after `toolName__`). Matching on the bare id means a
// different prefix spelling (e.g. `canvas_browser__runCanvasBrowserAction`)
// cannot slip past the gate.
const GATED_BARE_SET = new Set<string>(
  PLAN_GATED_MUTATE_TOOL_IDS.map((id) => (id.includes('__') ? id.split('__').pop() || id : id))
)

export function isPlanApprovalStatus(
  value: unknown
): value is ChatPlanApprovalStatus {
  return (
    value === 'draft' ||
    value === 'awaiting_approval' ||
    value === 'active' ||
    value === 'completed' ||
    value === 'cleared' ||
    value === 'rejected'
  )
}

export function isPlanAwaitingApproval(status: unknown): boolean {
  return status === PLAN_AWAITING_APPROVAL
}

export function isMutateToolGatedByPlan(toolId: unknown): boolean {
  if (typeof toolId !== 'string' || !toolId.trim()) {
    return false
  }

  const bare = toolId.includes('__') ? toolId.split('__').pop() || toolId : toolId
  return GATED_SET.has(bare) || GATED_SET.has(toolId) || GATED_BARE_SET.has(bare)
}

export function parseProposedPlanSteps(value: unknown): ProposedPlanStep[] {
  if (!Array.isArray(value)) {
    return []
  }

  const steps: ProposedPlanStep[] = []

  for (let index = 0; index < value.length; index += 1) {
    const item = value[index]
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      continue
    }

    const record = item as Record<string, unknown>
    const title =
      typeof record.title === 'string'
        ? record.title.trim()
        : typeof record.label === 'string'
          ? record.label.trim()
          : ''

    if (!title) {
      continue
    }

    const kind: PlanStepKind =
      record.kind === 'mutate' ||
      record.type === 'mutate' ||
      record.action === 'mutate'
        ? 'mutate'
        : 'read'

    const id =
      typeof record.id === 'string' && record.id.trim()
        ? record.id.trim()
        : `step-${index + 1}`

    const detail =
      typeof record.detail === 'string'
        ? record.detail
        : typeof record.description === 'string'
          ? record.description
          : null

    steps.push({ id, title, kind, detail })
  }

  return steps
}

export function formatProposedPlanMarkdown(args: {
  title?: string | null
  summary?: string | null
  steps: ProposedPlanStep[]
}): string {
  const lines: string[] = []
  const title = args.title?.trim() || 'Proposed plan'
  lines.push(`# ${title}`)

  if (args.summary?.trim()) {
    lines.push('')
    lines.push(args.summary.trim())
  }

  if (args.steps.length > 0) {
    lines.push('')
    lines.push('## Steps')
    for (const [index, step] of args.steps.entries()) {
      const kindLabel = step.kind === 'mutate' ? 'mutate' : 'read'
      lines.push(`${index + 1}. **[${kindLabel}]** ${step.title}`)
      if (step.detail?.trim()) {
        lines.push(`   ${step.detail.trim()}`)
      }
    }
  }

  lines.push('')
  lines.push(PLAN_AWAITING_MARKDOWN_FOOTER)
  return lines.join('\n')
}

export function buildProposePlanPayload(args: {
  title?: string | null
  summary?: string | null
  steps: unknown
}): {
  content: string
  steps: ProposedPlanStep[]
  status: typeof PLAN_AWAITING_APPROVAL
} {
  const steps = parseProposedPlanSteps(args.steps)
  if (steps.length === 0) {
    throw new Error('propose_plan requires at least one step')
  }

  return {
    content: formatProposedPlanMarkdown({
      title: args.title,
      summary: args.summary,
      steps
    }),
    steps,
    status: PLAN_AWAITING_APPROVAL
  }
}

export function filterToolsForPlanGate<T extends { function?: { name?: string }; name?: string }>(
  tools: T[],
  planStatus: unknown
): T[] {
  if (!isPlanAwaitingApproval(planStatus)) {
    return tools
  }

  return tools.filter(tool => {
    const name = tool.function?.name || tool.name
    return !isMutateToolGatedByPlan(name)
  })
}
